import React, { useRef, useState, useEffect } from 'react';

interface AnimatedBookCoverProps {
  coverImageUrl: string;
  coverAnimationUrl?: string | null;
  title: string;
  author: string;
  containerClassName?: string;
  imageClassName?: string;
  videoClassName?: string;
  imageStyle?: React.CSSProperties;
  videoStyle?: React.CSSProperties;
  autoPlayOnLoad?: boolean;
  onClick?: () => void;
}

const AnimatedBookCover: React.FC<AnimatedBookCoverProps> = ({
  coverImageUrl,
  coverAnimationUrl,
  title,
  author,
  containerClassName = '',
  imageClassName = '',
  videoClassName = '',
  imageStyle = {},
  videoStyle = {},
  autoPlayOnLoad = false,
  onClick
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasVideo, setHasVideo] = useState<boolean | null>(null);
  const [showVideo, setShowVideo] = useState(false);
  const coverVideoPath = coverAnimationUrl || '';

  // Prefetch video on mount
  useEffect(() => {
    if (!coverVideoPath) {
      setHasVideo(false);
      return;
    }

    const video = document.createElement('video');
    video.src = coverVideoPath;
    video.preload = 'auto';

    video.onloadeddata = () => {
      setHasVideo(true);
    };

    video.onerror = () => {
      setHasVideo(false);
    };
  }, [coverVideoPath]);

  // Auto-play video as soon as it is available for viewports that opt into it.
  useEffect(() => {
    if (!autoPlayOnLoad || !hasVideo) {
      return;
    }

    setShowVideo(true);
    videoRef.current?.play().catch(error => {
      console.log('Video play failed:', error);
    });
  }, [autoPlayOnLoad, hasVideo]);

  const handleMouseEnter = () => {
    if (!autoPlayOnLoad && hasVideo && videoRef.current) {
      setShowVideo(true);
      videoRef.current.play().catch(error => {
        console.log('Video play failed:', error);
      });
    }
  };

  const handleMouseLeave = () => {
    if (!autoPlayOnLoad && videoRef.current && hasVideo) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setShowVideo(false);
    }
  };

  return (
    <div
      role="button"
      className={containerClassName}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <img
        src={coverImageUrl}
        alt={`${title} by ${author} - Book Cover`}
        className={`${imageClassName} transition-opacity duration-500`}
        style={imageStyle}
      />
      {hasVideo && coverVideoPath && (
        <video
          ref={videoRef}
          src={coverVideoPath}
          className={`${videoClassName} absolute top-0 left-0 transition-opacity duration-500`}
          style={{
            ...videoStyle,
            opacity: showVideo ? 1 : 0,
            pointerEvents: showVideo ? 'auto' : 'none'
          }}
          loop
          muted
          playsInline
          aria-label={`${title} by ${author} - Animated Book Cover`}
        >
          Your browser does not support the video tag.
        </video>
      )}
    </div>
  );
};

export default AnimatedBookCover;
